import React, { useEffect, useState } from 'react';
import {
    Box, Button, Chip, IconButton, Paper, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Typography
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';
import { usePermission } from '../../hooks/usePermission';
import { UserType } from '../../interfaces/userType.enum';
import { Shift } from '../../interfaces/shift/Shift';
import { ShiftService } from '../../services/shift.service';
import ShiftDetailDialog from './ShiftDetailDialog';

const ShiftListPage: React.FC = () => {
    const { user, selectedInstitutionId } = useAuth();
    const toast = useToast();
    const { t } = useTranslation(['shifts', 'common']);

    const canCreate = usePermission('SHIFT_CREATE', user?.roles.includes(UserType.InstitutionAdmin) ?? false);
    const canEdit   = usePermission('SHIFT_EDIT',   user?.roles.includes(UserType.InstitutionAdmin) ?? false);
    const canDelete = usePermission('SHIFT_DELETE', user?.roles.includes(UserType.InstitutionAdmin) ?? false);

    const [shifts, setShifts] = useState<Shift[]>([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingShift, setEditingShift] = useState<Shift | null>(null);

    useEffect(() => {
        if (selectedInstitutionId) {
            fetchShifts();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedInstitutionId]);

    const fetchShifts = async () => {
        if (!selectedInstitutionId) return;
        setLoading(true);
        try {
            const data = await ShiftService.list(selectedInstitutionId);
            setShifts(data);
        } catch (err) {
            toast.showApiError(err, t('shiftList.fetchFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setEditingShift(null);
        setDialogOpen(true);
    };

    const handleEdit = (shift: Shift) => {
        setEditingShift(shift);
        setDialogOpen(true);
    };

    const handleDelete = async (shift: Shift) => {
        if (!selectedInstitutionId) return;
        if (!window.confirm(t('shiftList.confirmDelete', { name: shift.name }))) return;
        try {
            await ShiftService.remove(selectedInstitutionId, shift.id);
            toast.showSuccess(t('shiftList.deleted'));
            await fetchShifts();
        } catch (err) {
            toast.showApiError(err, t('shiftList.deleteFailed'));
        }
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setEditingShift(null);
    };

    const handleSaved = () => {
        fetchShifts();
    };

    // Format "HH:mm:ss" → "HH:mm"
    const fmtTime = (time: string) => (time ? time.substring(0, 5) : '—');

    if (loading) return <Typography sx={{ p: 3 }}>{t('loading')}</Typography>;

    return (
        <Box sx={{ padding: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">{t('shiftList.title')}</Typography>
                {canCreate && (
                    <Button variant="contained" color="primary" onClick={handleCreate}>
                        {t('shiftList.newShift')}
                    </Button>
                )}
            </Box>

            {shifts.length === 0 ? (
                <Typography>{t('shiftList.empty')}</Typography>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('common:fields.name')}</TableCell>
                                <TableCell>{t('shiftList.columns.code')}</TableCell>
                                <TableCell>{t('shiftList.columns.start')}</TableCell>
                                <TableCell>{t('shiftList.columns.end')}</TableCell>
                                <TableCell>{t('shiftList.columns.crossMidnight')}</TableCell>
                                <TableCell>{t('columns.active')}</TableCell>
                                {(canEdit || canDelete) && <TableCell>{t('common:fields.actions')}</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {shifts.map(shift => (
                                <TableRow key={shift.id}>
                                    <TableCell>{shift.name}</TableCell>
                                    <TableCell>{shift.code}</TableCell>
                                    <TableCell>{fmtTime(shift.startTime)}</TableCell>
                                    <TableCell>{fmtTime(shift.endTime)}</TableCell>
                                    <TableCell>
                                        {shift.crossMidnight
                                            ? <Chip label={t('common:actions.yes')} color="warning" size="small" />
                                            : <Chip label={t('common:actions.no')} size="small" />}
                                    </TableCell>
                                    <TableCell>
                                        {shift.isActive
                                            ? <Chip label={t('common:status.active')} color="success" size="small" />
                                            : <Chip label={t('common:status.inactive')} size="small" />}
                                    </TableCell>
                                    {(canEdit || canDelete) && (
                                        <TableCell>
                                            {canEdit && (
                                                <IconButton
                                                    onClick={() => handleEdit(shift)}
                                                    color="primary"
                                                    aria-label={t('ariaEdit')}
                                                    size="small"
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            )}
                                            {canDelete && (
                                                <IconButton
                                                    onClick={() => handleDelete(shift)}
                                                    color="error"
                                                    aria-label={t('ariaDelete')}
                                                    size="small"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {selectedInstitutionId && (
                <ShiftDetailDialog
                    open={dialogOpen}
                    onClose={handleDialogClose}
                    shift={editingShift}
                    institutionId={selectedInstitutionId}
                    onSaved={handleSaved}
                />
            )}
        </Box>
    );
};

export default ShiftListPage;
